<template>
  <CatalogCrudView
    title="Contas do ledger"
    subtitle="Configure os pontos internos de entrada e saída de valores do sistema financeiro."
    singular="conta do ledger"
    new-label="Nova conta do ledger"
    list-title="Contas do ledger cadastradas"
    icon="book"
    item-name-field="name"
    default-sort-by="name"
    search-placeholder="Nome, código, tipo, proprietário, banco ou moeda"
    record-lock-field="hasLedgerEntries"
    delete-blocked-field="hasLedgerEntries"
    delete-blocked-title="A conta possui lançamentos e deve ser inativada em vez de excluída."
    :service="service"
    :fields="fields"
    :columns="columns"
    :form-defaults="formDefaults"
    @field-change="handleFieldChange"
    @record-loaded="handleRecordLoaded"
    @record-new="resetBankOptions"
  />
</template>

<script>
import Swal from 'sweetalert2'
import CatalogCrudView from '../components/CatalogCrudView.vue'
import service from '../services/ledgerAccountService'

const inactiveLabel = option => option.isActive ? option.name : `${option.name} (inativo)`

export default {
  name: 'LedgerAccountsView',
  components: { CatalogCrudView },
  data: () => ({
    service,
    accountTypeOptions: [{ value: 0, label: 'Selecione o tipo', disabled: true }],
    ownerOptions: [{ value: 0, label: 'Selecione a entidade', disabled: true }],
    bankAccountOptions: [{ value: null, label: 'Nenhuma conta bancária' }],
    formDefaults: {
      accountTypeId: 0,
      ownerId: 0,
      bankAccountId: null,
      name: '',
      code: '',
      currencyCode: 'BRL',
      allowNegative: false,
      isSystem: false,
      isActive: true,
      hasLedgerEntries: false
    },
    columns: [
      { text: 'Id', value: 'id' },
      { text: 'Nome', value: 'name', emphasis: true },
      { text: 'Código', value: 'code', type: 'code' },
      { text: 'Tipo', value: 'accountTypeName' },
      { text: 'Proprietário', value: 'ownerName' },
      { text: 'Conta bancária', value: 'bankAccountName' },
      { text: 'Moeda', value: 'currencyCode', type: 'code' },
      { text: 'Permite negativo', value: 'allowNegative', type: 'boolean' },
      { text: 'Conta do sistema', value: 'isSystem', type: 'boolean' },
      { text: 'Movimentada', value: 'hasLedgerEntries', type: 'boolean', sortable: false },
      { text: 'Situação', value: 'isActive', type: 'status' },
      { text: 'Criado em', value: 'createdAt', type: 'date' }
    ]
  }),
  computed: {
    fields () {
      return [
        { key: 'name', label: 'Nome', required: true, maxlength: 150, placeholder: 'Ex.: Banco GarantePay', span: 5 },
        { key: 'code', label: 'Código', required: true, maxlength: 50, placeholder: 'Ex.: ACC_GP_BANCO', uppercase: true, lockWhenUsed: true, span: 4 },
        { key: 'currencyCode', label: 'Moeda', required: true, maxlength: 3, placeholder: 'BRL', uppercase: true, lockWhenUsed: true, span: 3 },
        { key: 'accountTypeId', label: 'Tipo de conta', required: true, type: 'select', options: this.accountTypeOptions, lockWhenUsed: true, span: 4 },
        { key: 'ownerId', label: 'Entidade proprietária', required: true, type: 'select', options: this.ownerOptions, lockWhenUsed: true, span: 4 },
        { key: 'bankAccountId', label: 'Conta bancária vinculada', type: 'select', options: this.bankAccountOptions, lockWhenUsed: true, span: 4 },
        { key: 'allowNegative', label: 'Permite saldo negativo', type: 'select', options: [{ value: false, label: 'Não' }, { value: true, label: 'Sim' }], span: 4 },
        { key: 'isSystem', label: 'Conta interna do sistema', type: 'select', options: [{ value: false, label: 'Não' }, { value: true, label: 'Sim' }], lockWhenUsed: true, span: 4 },
        { key: 'isActive', label: 'Situação', type: 'select', options: [{ value: true, label: 'Ativo' }, { value: false, label: 'Inativo' }], span: 4 }
      ]
    }
  },
  created () {
    this.loadBaseOptions()
  },
  methods: {
    mapOptions (options, placeholder) {
      return [
        { value: 0, label: placeholder, disabled: true },
        ...options.map(option => ({ value: option.id, label: inactiveLabel(option), disabled: !option.isActive }))
      ]
    },
    async loadBaseOptions () {
      try {
        const [accountTypes, owners] = await Promise.all([
          service.getAccountTypeOptions(),
          service.getOwnerOptions()
        ])
        this.accountTypeOptions = this.mapOptions(accountTypes, 'Selecione o tipo')
        this.ownerOptions = this.mapOptions(owners, 'Selecione a entidade')
      } catch (_) {
        await Swal.fire({ title: 'Atenção', text: 'Não foi possível carregar os tipos de conta e as entidades financeiras.', icon: 'error', confirmButtonColor: '#e77900' })
      }
    },
    async loadBankOptions (ownerId) {
      this.resetBankOptions()
      if (!ownerId) return
      try {
        const options = await service.getBankAccountOptions(ownerId)
        this.bankAccountOptions = [
          { value: null, label: 'Nenhuma conta bancária' },
          ...options.map(option => ({ value: option.id, label: inactiveLabel(option), disabled: !option.isActive }))
        ]
      } catch (_) {
        await Swal.fire({ title: 'Atenção', text: 'Não foi possível carregar as contas bancárias da entidade.', icon: 'error', confirmButtonColor: '#e77900' })
      }
    },
    resetBankOptions () {
      this.bankAccountOptions = [{ value: null, label: 'Nenhuma conta bancária' }]
    },
    async handleFieldChange ({ key, value, form }) {
      if (key !== 'ownerId') return
      form.bankAccountId = null
      await this.loadBankOptions(value)
    },
    async handleRecordLoaded (record) {
      await this.loadBankOptions(record.ownerId)
    }
  }
}
</script>
